// Pure rules for the Recurring tab and the forward-looking Runway. No React and
// no Graph calls: Finance.js loads Finance_Recurring, Finance_Transactions and
// Finance_Accounts with the user's token and hands the rows in. The page only
// reads these lists — nothing here plans a write.
//
// Binding rule (FAMILY_Finance_Spec_v5): Trust is client money. A Trust
// transaction never attaches to an item, and an item whose Context is not one
// of the three household contexts is dropped before anything is added up.
//
// Dates are 'YYYY-MM-DD' strings throughout (string-safe — a SharePoint
// dateOnly value is sliced, never converted through UTC).

import { shiftMonth, trailingBurn } from './accounts';

export const RECURRING_CONTEXTS = ['Personal', 'FIG', 'Monduras'];   // never Trust

// Graph stores no column default, so a blank AmountTolerancePct means this.
export const DEFAULT_TOLERANCE_PCT = 5;

// Finance_Categories id of "Housing": the rent rows. They sit outside the
// "Recurring bills" headline and inside "Incl. housing".
export const HOUSING_CATEGORY_ID = 4;

export const MISSED_GRACE_DAYS = 5;    // no charge this long after the expected date → Missed (amber)
export const MISSED_RED_DAYS   = 15;   // …and red from here
export const UNMATCHED_MONTHS  = 12;   // how far back the unmatched-charge section looks
export const RUNWAY_MAX_MONTHS = 120;

// What one occurrence is worth per month. Irregular has no cadence to multiply
// by: there, ExpectedAmount already IS the monthly average.
export const MONTHLY_FACTOR = Object.freeze({ Weekly: 52 / 12, Biweekly: 26 / 12, Monthly: 1, Quarterly: 1 / 3, Annual: 1 / 12, Irregular: 1 });
export const monthlyEquivalent = (item) => (item?.ExpectedAmount ?? 0) * (MONTHLY_FACTOR[item?.Cadence] ?? 0);

// How far one occurrence is from the next.
const STEP = { Weekly: { days: 7 }, Biweekly: { days: 14 }, Monthly: { months: 1 }, Quarterly: { months: 3 }, Annual: { months: 12 } };

// ── Dates ─────────────────────────────────────────────────────────────────────
const dayOf   = (v) => String(v || '').slice(0, 10);
const monthOf = (v) => String(v || '').slice(0, 7);
const toUTC   = (d) => { const [y, m, dd] = d.split('-').map(Number); return Date.UTC(y, m - 1, dd); };
const pad     = (n) => String(n).padStart(2, '0');

export const daysBetween = (a, b) => Math.round((toUTC(b) - toUTC(a)) / 86400000);
export const addDays     = (d, n) => new Date(toUTC(d) + n * 86400000).toISOString().slice(0, 10);
const daysInMonth = (ym) => { const [y, m] = ym.split('-').map(Number); return new Date(Date.UTC(y, m, 0)).getUTCDate(); };
// The 31st lands on the last day of a shorter month.
const dayInMonth  = (ym, dom) => `${ym}-${pad(Math.min(dom, daysInMonth(ym)))}`;
export const addMonths = (d, n) => dayInMonth(shiftMonth(monthOf(d), n), Number(d.slice(8, 10)));
const monthsApart = (a, b) => { const [ay, am] = a.split('-').map(Number), [by, bm] = b.split('-').map(Number); return (by - ay) * 12 + (bm - am); };

// A Date → 'YYYY-MM-DD' from LOCAL parts (toISOString would shift the day).
export const localDay = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// ── Merchant cleaner ──────────────────────────────────────────────────────────
// A port of cleanMerchant in the server's finance-recurring.js, which is what
// wrote every CleanMerchant MatchValue on the list. It is NOT the page's
// normalizeMerchant: that one reads the Title only, and most MatchValues are
// Plaid's MerchantName ("LifeCafe at Life Time", "Disney+") — a name the
// Title never contains. Keep the two copies in step.
//   1. A long ACH row names its originator ("ORIG CO NAME:ADP Tax …").
//   2. Otherwise Plaid's MerchantName, when it has one.
//   3. Otherwise the descriptor with its noise stripped.
export function cleanMerchant(t = {}) {
  const title = String(t.Title || '').trim();
  const ach = title.match(/^ORIG CO NAME:(.+?)\s+ORIG ID:/i);
  if (ach) return ach[1].trim();
  if (t.MerchantName && String(t.MerchantName).trim()) return String(t.MerchantName).trim();
  let s = title
    .replace(/\s+\d{2}\/\d{2}$/, '')                       // trailing purchase date
    .replace(/^Online Payment \d+ To\s+/i, '')             // keep the payee
    .replace(/\s+(PPD|WEB|CCD|TEL) ID:\s*\w+$/i, '')       // short ACH id
    .replace(/\s+FOR THE MONTH OF \w+$/i, '')              // monthly service charge
    .replace(/\s+card\s+\d+/gi, '');
  if (!/^CHECK #/i.test(s)) s = s.split('#')[0].replace(/\b\d{5,}\b.*/, '').replace(/\s+[A-Z]{2}$/, '');
  s = s.replace(/\s{2,}/g, ' ').trim();
  return s || title || 'Unknown';
}

// ── Matching ──────────────────────────────────────────────────────────────────
const matchValue = (item) => String(item.MatchValue || '').trim().toLowerCase();
const tolerance  = (item) => (Number.isFinite(item.AmountTolerancePct) ? item.AmountTolerancePct : DEFAULT_TOLERANCE_PCT) / 100;

// Within the item's tolerance of its ExpectedAmount (half a cent of slack for
// the arithmetic).
export const fits = (item, amount) => Math.abs(amount - item.ExpectedAmount) <= item.ExpectedAmount * tolerance(item) + 0.005;

// The server names a row it split off a merchant by amount "Merchant ($9.99)".
const SPLIT_TITLE = /\(\$[\d,]+(\.\d{1,2})?\)\s*$/;

// Items that transactions attach to: Active and Ended, household contexts only.
const isTracked = (item) => (item.Status === 'Active' || item.Status === 'Ended') && RECURRING_CONTEXTS.includes(item.Context);

// An item is billed to an account (AccountMask). Its Context can differ from
// that account's — Shopify is Monduras, billed to FIG's 1758 — so the mask
// decides; Context only stands in when a row has no mask.
const itemAccount = (item) => (item.AccountMask ? `mask:${item.AccountMask}` : `ctx:${item.Context}`);

// Attaches Finance_Transactions to recurring items.
//   • Only money going out, never Trust.
//   • TitleContains: the MatchValue appears in the Title.
//   • CleanMerchant: the cleaned merchant equals the MatchValue. A merchant
//     with several items — or one item the server split off by amount
//     ("Anthropic ($213.20)") — is told apart by amount: a charge goes to the
//     item it fits (nearest ExpectedAmount when it fits two). A charge that
//     fits none is an UNMATCHED charge from a known merchant: it attaches to
//     nothing and is listed for review.
//   • An Irregular item matches by merchant only, whatever the amount.
// maskById: Finance_Accounts id → mask.
//   → { byItem: Map(itemId → [transaction]), unmatched: [{ id, date, amount, merchant, mask, context }],
//       matchedIds: Set(transactionId), byAmount: Set(itemId) }
export function matchRecurring(transactions = [], items = [], maskById = {}) {
  const tracked = (items || []).filter(isTracked);
  const byItem = new Map(tracked.map(i => [i.id, []]));
  const matchedIds = new Set(), byAmount = new Set(), unmatched = [];

  const titleItems = tracked.filter(i => i.MatchType === 'TitleContains' && matchValue(i));
  const groups = new Map();
  for (const i of tracked) {
    if (i.MatchType !== 'CleanMerchant' || !matchValue(i)) continue;
    const key = `${matchValue(i)}|${itemAccount(i)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(i);
  }
  const amountKeyed = (group) => group.length > 1 || group.some(i => SPLIT_TITLE.test(i.Title || ''));
  for (const group of groups.values()) {
    if (amountKeyed(group)) for (const i of group) if (i.Cadence !== 'Irregular') byAmount.add(i.id);
  }

  const attach = (item, t) => { byItem.get(item.id).push(t); matchedIds.add(t.id); };
  const onAccount = (item, t, mask) => (item.AccountMask ? String(item.AccountMask) === mask : item.Context === t.Context);

  for (const t of transactions || []) {
    if (t.Direction !== 'Out' || t.Context === 'Trust') continue;
    const mask  = String(maskById[String(t.AccountLookupId)] ?? '');
    const title = String(t.Title || '').toLowerCase();

    const byTitle = titleItems.find(i => title.includes(matchValue(i)) && onAccount(i, t, mask));
    if (byTitle) { attach(byTitle, t); continue; }

    const merchant = cleanMerchant(t);
    const group = groups.get(`${merchant.toLowerCase()}|mask:${mask}`) || groups.get(`${merchant.toLowerCase()}|ctx:${t.Context}`);
    if (!group) continue;
    if (!amountKeyed(group)) { attach(group[0], t); continue; }

    const amount = t.Amount ?? 0;
    const fit = group
      .filter(i => i.Cadence !== 'Irregular' && fits(i, amount))
      .sort((a, b) => Math.abs(amount - a.ExpectedAmount) - Math.abs(amount - b.ExpectedAmount))[0]
      || group.find(i => i.Cadence === 'Irregular');
    if (fit) attach(fit, t);
    else unmatched.push({ id: t.id, date: dayOf(t.Date), amount, merchant, mask, context: group[0].Context });
  }
  return { byItem, unmatched, matchedIds, byAmount };
}

// What was paid, oldest first. Charges that post the same day are ONE payment
// (Allstate bills two policies on one date), except on an item told apart by
// amount, where every charge stands alone — two there really are two.
export function paymentsOf(txs = [], perCharge = false) {
  const rows = txs.map(t => ({ date: dayOf(t.Date), amount: t.Amount ?? 0 })).filter(p => p.date);
  rows.sort((a, b) => a.date.localeCompare(b.date));
  if (perCharge) return rows;
  const out = [];
  for (const r of rows) {
    const cur = out[out.length - 1];
    if (cur && cur.date === r.date) cur.amount = Math.round((cur.amount + r.amount) * 100) / 100;
    else out.push({ ...r });
  }
  return out;
}

// ── Status ────────────────────────────────────────────────────────────────────
const hasEnded   = (item, today) => item.Status === 'Ended' || (!!item.EndDate && dayOf(item.EndDate) < today);
// "Scheduled items start at StartDate" — one with no date has not started.
const hasStarted = (item, today) => (item.StartDate ? dayOf(item.StartDate) <= today : item.Scheduled !== true);
const isPrepaid  = (item, today) => !!item.PaidThrough && dayOf(item.PaidThrough) >= today;

// Counted in today's totals: Active, begun, not over. (A prepaid item IS
// counted here — its monthly cost is real; only Runway treats it as $0.)
export const activeNow = (item, today) => item.Status === 'Active' && RECURRING_CONTEXTS.includes(item.Context) && hasStarted(item, today) && !hasEnded(item, today);

function missedOrDue(due, today) {
  const late = daysBetween(due, today);
  if (late > MISSED_GRACE_DAYS) return { kind: 'missed', date: due, tone: late >= MISSED_RED_DAYS ? 'red' : 'amber' };
  return { kind: 'due', date: due };
}

// A monthly bill is judged by billing cycle, not calendar month: a payment
// belongs to the expected date it is nearest. (The Sep 1 water bill that posts
// Aug 31 is September's — not a second August charge and a missed September.)
function nearestDue(date, dom) {
  const ym = monthOf(date);
  const own = dayInMonth(ym, dom);
  let best = own;
  for (const c of [dayInMonth(shiftMonth(ym, -1), dom), dayInMonth(shiftMonth(ym, 1), dom)]) {
    if (Math.abs(daysBetween(c, date)) < Math.abs(daysBetween(best, date))) best = c;
  }
  return best;
}

// → { status: { kind: 'paid' | 'due' | 'double' | 'missed', date?, tone? } | null, next: date | null }
function cycle(item, payments, today) {
  const ym = monthOf(today);
  const last = payments[payments.length - 1] || null;
  const step = STEP[item.Cadence];

  // No cadence, no expected date: all that can be said is whether it has come.
  if (!step) return { status: last && monthOf(last.date) === ym ? { kind: 'paid' } : null, next: null };

  if (item.Cadence === 'Monthly' && Number.isFinite(item.DayOfMonth)) {
    const dom = item.DayOfMonth;
    const due = dayInMonth(ym, dom), prevDue = dayInMonth(shiftMonth(ym, -1), dom);
    const paidOn = (d) => payments.filter(p => nearestDue(p.date, dom) === d).length;
    const n = paidOn(due);
    const nextDue = dayInMonth(shiftMonth(ym, 1), dom);
    if (n >= 2) return { status: { kind: 'double' }, next: nextDue };
    if (n === 1) return { status: { kind: 'paid' }, next: nextDue };
    // Nothing yet this cycle. A bill that never came LAST cycle stays Missed
    // until this cycle's date arrives — otherwise a bill due on the 29th could
    // never show as missed: the month turns over before the grace runs out.
    const hadHistory = payments.some(p => p.date < prevDue) && (!item.StartDate || dayOf(item.StartDate) <= prevDue);
    if (today < due && hadHistory && paidOn(prevDue) === 0) return { status: missedOrDue(prevDue, today), next: prevDue };
    return { status: missedOrDue(due, today), next: due };
  }

  // Everything else runs from the last payment.
  if (!last) return { status: null, next: null };
  const next = step.months ? addMonths(last.date, step.months) : addDays(last.date, step.days);
  if (daysBetween(next, today) > MISSED_GRACE_DAYS) return { status: missedOrDue(next, today), next };
  const thisMonth = payments.filter(p => monthOf(p.date) === ym).length;
  if (step.months && thisMonth >= 2) return { status: { kind: 'double' }, next };
  if (thisMonth >= 1) return { status: { kind: 'paid' }, next };
  // Quarterly and Annual carry their date in the Next column — never "Due" every month.
  return { status: step.months >= 3 ? null : { kind: 'due', date: next }, next };
}

// The first expected date on or after the item's StartDate.
function firstDue(item) {
  const start = dayOf(item.StartDate);
  if (!start) return null;
  if (item.Cadence !== 'Monthly' || !Number.isFinite(item.DayOfMonth)) return start;
  const due = dayInMonth(monthOf(start), item.DayOfMonth);
  return due >= start ? due : dayInMonth(shiftMonth(monthOf(start), 1), item.DayOfMonth);
}

// What actually went out per month over the trailing 12 full months. An item
// whose first charge falls inside that window is averaged over the months it
// has existed — but never fewer than one full cadence, or a single annual
// charge two months ago would read as half of it per month.
function trailingAverage(item, payments, today) {
  if (!payments.length) return null;
  const end = shiftMonth(monthOf(today), -1), start = shiftMonth(monthOf(today), -12);
  const first = monthOf(payments[0].date);
  if (first > end) return null;
  const total = payments.filter(p => monthOf(p.date) >= start && monthOf(p.date) <= end).reduce((s, p) => s + p.amount, 0);
  const existed = monthsApart(first > start ? first : start, end) + 1;
  const floor = { Quarterly: 3, Annual: 12 }[item.Cadence] || 1;
  return total / Math.min(12, Math.max(existed, floor));
}

// One line of the tab.
//   → { item, monthly, activeNow, ended, last, next, avg12, badges: [{ kind, date?, tone? }] }
// Badge kinds: paid · due · double · missed · price · prepaid · starts · ends · ended
export function recurringRow(item, txs = [], { today, byAmount = false } = {}) {
  const payments = paymentsOf(txs, byAmount);
  const last = payments[payments.length - 1] || null;
  const ended = hasEnded(item, today), started = hasStarted(item, today), prepaid = isPrepaid(item, today);
  const end = dayOf(item.EndDate);
  const badges = [];
  let next = null;

  if (ended) {
    badges.push({ kind: 'ended', date: end || null });
  } else if (!started) {
    badges.push({ kind: 'starts', date: dayOf(item.StartDate) || null });
    next = firstDue(item);
  } else if (prepaid) {
    badges.push({ kind: 'prepaid', date: dayOf(item.PaidThrough) });
  } else {
    const c = cycle(item, payments, today);
    next = c.next;
    if (c.status) badges.push(c.status);
    // An amount-keyed item only ever holds charges that fit; an Irregular one
    // has no per-charge amount to hold a charge to.
    if (last && !byAmount && STEP[item.Cadence] && !fits(item, last.amount)) badges.push({ kind: 'price' });
  }
  if (!ended && end && !(prepaid && dayOf(item.PaidThrough) === end)) badges.push({ kind: 'ends', date: end });
  if (next && end && next > end) next = null;

  return {
    item, monthly: monthlyEquivalent(item), activeNow: activeNow(item, today), ended,
    last, next, avg12: trailingAverage(item, payments, today), badges,
  };
}

// Every Active or Ended household item as a row.
export function recurringRows(items = [], match, today) {
  return (items || []).filter(isTracked).map(i => recurringRow(i, match.byItem.get(i.id) || [], { today, byAmount: match.byAmount.has(i.id) }));
}

// The most urgent status among a category's rows, for its header: Missed, then
// 2×, then Price changed, then Due — with how many items show it. Null when
// none does. A Missed summary is red as soon as one of them is.
const URGENCY = ['missed', 'double', 'price', 'due'];
export function mostUrgent(rows = []) {
  for (const kind of URGENCY) {
    const hits = rows.map(r => r.badges.find(b => b.kind === kind)).filter(Boolean);
    if (!hits.length) continue;
    return kind === 'missed'
      ? { kind, count: hits.length, tone: hits.some(b => b.tone === 'red') ? 'red' : 'amber' }
      : { kind, count: hits.length };
  }
  return null;
}

// Whether a category the user has never folded or unfolded starts open: only
// when something in it needs a look — Missed, 2× or Price changed. A category
// that is merely Due, or has nothing to say, starts collapsed.
export const startsExpanded = (urgent) => !!urgent && urgent.kind !== 'due';

// The headline: what is being paid per month right now.
//   bills = everything but housing · inclHousing = bills + the rent rows.
export function recurringSummary(rows = []) {
  const now = rows.filter(r => r.activeNow);
  const isHousing = (r) => Number(r.item.Category) === HOUSING_CATEGORY_ID;
  const housingRows = now.filter(isHousing);
  const bills   = now.filter(r => !isHousing(r)).reduce((s, r) => s + r.monthly, 0);
  const housing = housingRows.reduce((s, r) => s + r.monthly, 0);
  return { bills, housing, inclHousing: bills + housing, hasHousing: housingRows.length > 0, housingEstimate: housingRows.some(r => r.item.IsEstimate === true) };
}

// Unmatched known-merchant charges from the last UNMATCHED_MONTHS, newest first.
export function recentUnmatched(unmatched = [], today) {
  const from = addMonths(today, -UNMATCHED_MONTHS);
  return unmatched.filter(u => u.date > from).sort((a, b) => b.date.localeCompare(a.date) || b.amount - a.amount);
}

// ── Forward-looking Runway ────────────────────────────────────────────────────
// An item counts toward month `ym` when it is Active and the month falls inside
// StartDate…EndDate (whole months — a bill that ends Nov 30 is in November and
// gone in December).
export function activeInMonth(item, ym) {
  if (item.Status !== 'Active' || !RECURRING_CONTEXTS.includes(item.Context)) return false;
  const start = monthOf(item.StartDate), end = monthOf(item.EndDate);
  if (item.Scheduled === true && !start) return false;
  return (!start || start <= ym) && (!end || end >= ym);
}

// Cash the recurring items take in month `ym`. A prepaid item costs nothing
// while the month is on or before its PaidThrough.
export function recurringInMonth(items = [], ym) {
  let total = 0;
  for (const i of items || []) {
    if (!activeInMonth(i, ym)) continue;
    if (i.PaidThrough && ym <= monthOf(i.PaidThrough)) continue;
    total += monthlyEquivalent(i);
  }
  return total;
}

// Spend that is NOT a recurring item: the trailing full months of consolidated
// spend (accounts.js trailingBurn, the same window Runway always used) less the
// part of it that attached to a recurring item, per month. Null under three
// full months of data.
export function variableSpend(transactions, isSpend, matchedIds, now = new Date()) {
  const all = trailingBurn(transactions, isSpend, now);
  if (!all) return null;
  const matched = trailingBurn(transactions, t => isSpend(t) && matchedIds.has(t.id), now);
  return { variable: all.burn - matched.burn, burn: all.burn, matched: matched.burn, months: all.months };
}

// Months until the cash is gone, with no income assumed. Each future month
// costs variable spend + the recurring items active in it; the walk starts
// today, so the month in progress counts only for the days left in it.
//   → { months: number | null, variable, bills (this month's recurring), capped }
export function forwardRunway(liquid, variable, items, today) {
  const ym = monthOf(today);
  const base = { months: null, variable, bills: recurringInMonth(items, ym), capped: false };
  if (!Number.isFinite(liquid) || !Number.isFinite(variable)) return base;
  if (liquid <= 0) return { ...base, months: 0 };

  let left = liquid, months = 0, m = ym;
  let share = (daysInMonth(ym) - Number(today.slice(8, 10)) + 1) / daysInMonth(ym);
  while (months < RUNWAY_MAX_MONTHS) {
    const out = (variable + recurringInMonth(items, m)) * share;
    if (out >= left) return { ...base, months: months + share * (left / out) };
    left -= out;
    months += share;
    share = 1;
    m = shiftMonth(m, 1);
  }
  return { ...base, months: RUNWAY_MAX_MONTHS, capped: true };
}

// The rent dates the Runway footnote names, read off the housing rows: a
// prepaid one still running, and a scheduled one still to start.
export function housingOutlook(items = [], today) {
  const housing = (items || []).filter(i => i.Status === 'Active' && Number(i.Category) === HOUSING_CATEGORY_ID);
  const prepaid = housing.find(i => isPrepaid(i, today));
  const upcoming = housing.filter(i => i.StartDate && dayOf(i.StartDate) > today).sort((a, b) => dayOf(a.StartDate).localeCompare(dayOf(b.StartDate)))[0];
  return { prepaidThrough: prepaid ? dayOf(prepaid.PaidThrough) : null, newRentFrom: upcoming ? dayOf(upcoming.StartDate) : null };
}
